// Shared CSS primitives lifted directly from the DS components/.
//
// Per `static/design-system/CLAUDE.md`, the DS is the single source of
// truth. Each export below is a 1:1 copy from a DS component file —
// using the same flat class names (`.chat-bubble--user`, `.chip`,
// `.tag`, `.provider-logo`, etc.). Inside Lit Shadow DOM these are
// component-private, so namespace pollution = zero.
//
// When a DS component CSS file changes, update the corresponding
// export here in lockstep (no autosync). Sync direction: DS → widget.
//
// Usage:
//
//   static styles = [designTokens, chatBubbleStyles, chipStyles, css`.local{}`];
//
// All values use design tokens (`var(--...)`). Adding a primitive that
// needs a value not in `designTokens` requires adding the token first
// (which means adding it to DS tokens.css too — keep them aligned).

import { css, type CSSResult } from 'lit';

/**
 * Chat bubble shapes — User, Emma, Result variants.
 * Source: design-system/components/chat-bubble.css.
 *
 * `.chat-row`, `.chat-row--user`, `.chat-row--emma` — outer row layouts
 * `.chat-bubble--user` — solid gray-60 ending bubble, user query
 * `.chat-bubble--emma` — gray-03 surface bubble, Emma response
 * `.chat-bubble--result` — collapsible result container
 *
 * Avatar animation classes (`.chat-avatar.animate`, `.chat-avatar.static`)
 * and SVG-specific `.logo-main` / `.logo-shadow` / `.logo-glare` keyframes
 * are NOT included here — they belong in T08+ when the avatar SVG ships.
 */
export const chatBubbleStyles: CSSResult = css`
  .chat-row {
    display: flex;
    gap: var(--space-2);
  }
  .chat-row--user {
    justify-content: flex-end;
    align-items: flex-end;
  }
  .chat-row--emma {
    align-items: flex-start;
  }

  .chat-bubble--user {
    background: var(--gray-60);
    border-radius: var(--radius-md);
    border-bottom-right-radius: 2px;
    padding: 10px 14px;
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    color: var(--gray-05);
    line-height: 1.5;
    max-width: 85%;
  }

  .chat-bubble--emma {
    flex: 1;
    min-width: 0;
    background: var(--gray-03);
    border-radius: var(--radius-md);
    border-top-left-radius: 2px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    font-size: var(--text-sm);
    color: var(--color-text);
    line-height: var(--leading-md);
  }

  .chat-bubble--result {
    flex: 1;
    min-width: 0;
    background: var(--gray-03);
    border-radius: var(--radius-md);
    border-top-left-radius: 2px;
    overflow: hidden;
  }
`;

/**
 * Chip variants from DS components/chips.css. Three flavors:
 *
 * - `.chip` (+ `.chip--active`) — default suggestion chip
 * - `.chip--refine` — pill-shaped in-conversation refine chip
 * - `.chip-preset` — landing preset chip (under input)
 *
 * Components include this when they render any chip-like element.
 */
export const chipStyles: CSSResult = css`
  .chip {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--gray-20);
    background: var(--white);
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    cursor: pointer;
    transition: border-color var(--duration-fast) var(--easing),
      color var(--duration-fast) var(--easing);
    white-space: nowrap;
    user-select: none;
  }
  .chip:hover {
    border-color: var(--blue-60);
    color: var(--color-text);
  }
  .chip--active {
    border-color: var(--blue-60);
    color: var(--blue-80);
    background: var(--blue-03);
  }

  .chip--refine {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: 4px 12px;
    border-radius: var(--radius-full);
    border: none;
    background: var(--gray-10);
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    color: var(--color-text-muted);
    cursor: pointer;
    transition: background var(--duration-fast) var(--easing),
      color var(--duration-fast) var(--easing);
    white-space: nowrap;
    user-select: none;
  }
  .chip--refine svg {
    flex-shrink: 0;
    opacity: 0.6;
  }
  .chip--refine:hover {
    background: var(--gray-15);
    color: var(--color-text);
  }

  .chip-preset {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--gray-15);
    background: var(--white);
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    cursor: pointer;
    transition: border-color var(--duration-fast) var(--easing),
      color var(--duration-fast) var(--easing);
  }
  .chip-preset:hover {
    border-color: var(--gray-40);
    color: var(--color-text);
  }
  .chip-preset svg {
    opacity: 0.35;
  }
`;

/**
 * Spec tags / badges — small mono-font pills attached to result cards.
 * Source: design-system/components/spec-tags.css.
 *
 * - `.tag` — neutral default
 * - `.tag--accent` — blue (typically for "USD", "EUR", "spot")
 * - `.tag--best` — green (for best-price marker)
 */
export const tagStyles: CSSResult = css`
  /* inline-flex (instead of inline-block) so tags with leading icons
     (e.g. spot tag with clock SVG) baseline-align icon and text. Single-
     text tags render identically — no visible difference from gap when
     there's only one child. */
  .tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--gray-15);
    font-family: var(--font-mono);
    font-size: var(--text-2xs);
    color: var(--gray-60);
    white-space: nowrap;
  }
  .tag--accent {
    border-color: var(--blue-15);
    color: var(--blue-80);
    background: var(--blue-03);
  }
  .tag--best {
    border-color: var(--green-15);
    color: var(--green-80);
    background: var(--green-03);
  }
`;

/**
 * Provider logo rounded badge — wraps the provider SVG/PNG inside a
 * 24x24 white circle with a subtle border.
 * Source: design-system/components/provider-logo.css.
 */
export const providerLogoStyles: CSSResult = css`
  .provider-logo {
    width: 24px;
    height: 24px;
    border-radius: 12px;
    border: 1px solid var(--gray-10);
    background: var(--white);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .provider-logo img {
    width: 11px;
    height: 11px;
  }
`;

/**
 * Inline CTA button (from DS chat-bubble.css `.chat-cta-inline`).
 * Used inside Emma response bubbles for action buttons that flow
 * with the surrounding text.
 */
export const ctaInlineStyles: CSSResult = css`
  .chat-cta-inline {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    margin-top: var(--space-1);
    padding: 8px 16px;
    border-radius: var(--radius-sm);
    background: var(--blue-accent);
    color: var(--white);
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    text-decoration: none;
    cursor: pointer;
    border: none;
    transition: background var(--duration-fast) var(--easing);
    white-space: nowrap;
  }
  @media (hover: hover) {
    .chat-cta-inline:hover {
      background: var(--blue-80);
    }
  }
  .chat-cta-inline:active {
    transform: scale(0.95);
  }
`;
